/**
 * 사용자 메뉴의 「프로젝트 내보내기」「프로젝트 불러오기」 — 동작과 진행 표시 (services/projectTransfer).
 *
 * 메뉴는 누르면 닫혀 사라지므로 파일 고르기 입력과 진행 창은 메뉴 밖(헤더)에 둔다 — 이 훅이 돌려주는 element.
 */
import React, { useRef, useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AppDispatch, RootState } from '../../store';
import { loadProjects, loadWorkflows, setSelectedProject } from '../../store/project/projectSlice';
import { showAlert, showConfirm } from '../common/AppDialog';
import { isCanvasDirty } from '../../utils/canvasSnapshot';
import { downloadJson, exportProject, fileNameOf, importIntoProject, importProject, parseProjectFile } from '../../services/projectTransfer';
import { logError, logUsage } from '../../services/eventLog';
import { formatNumber } from '../../utils/formatters';
import TeraStreamImportDialog, { MigrateTarget } from './TeraStreamImportDialog';
import type { ProjectFile } from '../../services/projectTransfer';

export function useProjectTransfer() {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const store = useStore<RootState>();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<{ label: string; done: number; total: number } | null>(null);
  const [migrateOpen, setMigrateOpen] = useState(false);     // 테라스트림에서 가져오기 (TeraStreamImportDialog)

  /** 이 프로젝트에 저장하지 않은 편집이 있는 워크플로 이름 — 내보내기는 서버에 저장된 것만 싣는다 */
  const unsavedNames = (projectId: string): string[] => {
    const s = store.getState();
    const cur = s.workflow.current?.id;
    return s.project.workflows.filter((w) => w.projectId === projectId).filter((w) => (w.id === cur
      ? isCanvasDirty(s.project.savedSnapshots[w.id], s.workflow.nodes, s.workflow.edges, s.workflow.settings)
      : w.nodes !== undefined && isCanvasDirty(s.project.savedSnapshots[w.id], w.nodes, w.edges, w.settings))).map((w) => w.name);
  };

  const doExport = async () => {
    const s = store.getState();
    const project = s.project.projects.find((p) => p.id === s.project.selectedProjectId);
    if (!project) return;
    const unsaved = unsavedNames(project.id);
    if (unsaved.length && !(await showConfirm(t('layout:transfer.unsavedConfirm', { names: unsaved.join(', ') }), { tone: 'warning', okText: t('layout:transfer.exportAnyway') }))) return;
    setBusy({ label: t('layout:transfer.exporting'), done: 0, total: 0 });
    try {
      const file = await exportProject(project, (done, total) => setBusy({ label: t('layout:transfer.exporting'), done, total }));
      const name = `${fileNameOf(project.name)}.openflow-project.json`;
      downloadJson(file, name);
      logUsage('project.export', { target: project.name, detail: { workflows: file.workflows.length } });
      showAlert(t('layout:transfer.exported', { file: name, n: formatNumber(file.workflows.length) }));
    } catch (e) {
      logError('project.export', e as Error, { target: project.name });
      showAlert(t('layout:transfer.exportFailed', { message: (e as Error)?.message || String(e) }));
    } finally {
      setBusy(null);
    }
  };

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    let file;
    try {
      file = parseProjectFile(JSON.parse(await f.text()));
    } catch (err) {
      showAlert(err instanceof SyntaxError ? t('layout:transfer.notProjectFile') : (err as Error).message);
      return;
    }
    await runImport(file);
  };

  /** 파일 내용으로 새 프로젝트 — 프로젝트 불러오기와 테라스트림 이관이 같이 쓴다 */
  const runImport = async (file: ProjectFile) => {
    const n = file.workflows.length;
    if (!(await showConfirm(t('layout:transfer.importConfirm', { name: file.project.name, n: formatNumber(n) }), { okText: t('layout:transfer.importOk') }))) return;
    const s = store.getState();
    setBusy({ label: t('layout:transfer.importing'), done: 0, total: n });
    try {
      const res = await importProject(file, s.project.projects.map((p) => p.name), s.auth.user?.usrid || '',
        (done, total) => setBusy({ label: t('layout:transfer.importing'), done, total }));
      await dispatch(loadProjects());
      dispatch(setSelectedProject(res.project.id));        // 불러온 프로젝트로 옮겨 바로 보이게
      logUsage('project.import', { target: res.project.name, detail: { created: res.created, failed: res.failed.length } });
      showAlert(res.failed.length
        ? t('layout:transfer.importedWithFailures', { name: res.project.name, n: formatNumber(res.created), failed: res.failed.map((x) => `${x.name}: ${x.message}`).join('\n') })
        : t('layout:transfer.imported', { name: res.project.name, n: formatNumber(res.created) }));
    } catch (err) {
      logError('project.import', err as Error, { target: file.project.name });
      showAlert(t('layout:transfer.importFailed', { message: (err as Error)?.message || String(err) }));
    } finally {
      setBusy(null);
    }
  };

  /**
   * 테라스트림 이관 결과 여러 개를 차례로 — 새 프로젝트면 테라스트림 프로젝트마다 하나, 지금 프로젝트면 그 안의 폴더에.
   * 하나가 실패해도 나머지는 계속하고, 끝에 모아서 알린다.
   */
  const runMigrationImport = async (files: ProjectFile[], target: MigrateTarget) => {
    const s = store.getState();
    const project = s.project.projects.find((p) => p.id === s.project.selectedProjectId);
    const total = files.reduce((n, f) => n + f.workflows.length, 0);
    // 테라스트림 프로젝트는 지금 프로젝트의 워크플로가 된다 — 프로젝트를 새로 만들지 않는다
    if (!project) { showAlert(t('layout:migrate.noCurrent'), { tone: 'warning' }); return; }
    const where = project.name;
    if (!(await showConfirm(t('layout:migrate.importConfirm', { n: formatNumber(files.length), workflows: formatNumber(total), where, folder: target.folder || '/' }),
      { okText: t('layout:transfer.importOk') }))) return;
    const usrid = s.auth.user?.usrid || '';
    const lines: string[] = [];
    let done = 0;
    setBusy({ label: t('layout:transfer.importing'), done: 0, total });
    for (const file of files) {
      const folder = target.perProject ? [target.folder, file.project.name].filter(Boolean).join('/') : target.folder;
      const base = done;
      const progress = (d: number) => setBusy({ label: t('layout:transfer.importing'), done: base + d, total });
      try {
        const res = await importIntoProject(file, project, usrid, { folder }, progress);
        lines.push(t('layout:migrate.importedLine', { name: file.project.name, n: formatNumber(res.created), folder: folder || '/' })
          + (res.failed.length ? `\n  ${res.failed.map((x) => `${x.name}: ${x.message}`).join('\n  ')}` : ''));
        logUsage('project.import', { target: file.project.name, detail: { created: res.created, failed: res.failed.length, migrate: 'terastream', mode: target.mode } });
      } catch (err) {
        logError('project.import', err as Error, { target: file.project.name });
        lines.push(t('layout:migrate.importedFail', { name: file.project.name, message: (err as Error)?.message || String(err) }));
      }
      done += file.workflows.length;
    }
    setBusy(null);
    await dispatch(loadWorkflows(project.id));
    showAlert(lines.join('\n'), { title: t('layout:migrate.title') });
  };

  const element = (
    <>
      <input ref={fileRef} type="file" accept=".json,application/json" className="hidden" onChange={onFile} />
      {migrateOpen && (
        <TeraStreamImportDialog onClose={() => setMigrateOpen(false)}
          onImport={(files, target) => { setMigrateOpen(false); runMigrationImport(files, target); }} />
      )}
      {busy && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-lg shadow-xl px-6 py-4 min-w-[18rem] text-sm text-gray-700">
            <div className="font-semibold mb-2">{busy.label}</div>
            <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
              <div className="h-full bg-blue-500 transition-all" style={{ width: busy.total ? `${(busy.done / busy.total) * 100}%` : '10%' }} />
            </div>
            <div className="mt-1 text-xs text-gray-500 tabular-nums">{busy.total ? `${formatNumber(busy.done)} / ${formatNumber(busy.total)}` : ' '}</div>
          </div>
        </div>
      )}
    </>
  );

  return { exportProject: doExport, importProject: () => fileRef.current?.click(), importTeraStream: () => setMigrateOpen(true), element };
}
